import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Gavel, Crown } from 'lucide-react';
import { EmptyState, getPositionStyle, ClubBadge } from './shared';

export const StandingsSection = ({ standings, isItalian }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3" data-testid="standings-section">
    {standings.length === 0 ? (
      <EmptyState icon={Trophy} text={isItalian ? 'Nessuna partita completata ancora' : 'No completed matches yet'} />
    ) : (
      <>
        {/* Desktop Table */}
        <div className="hidden md:block bg-[#0a0d15] border border-white/10 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 text-slate-500 text-[10px] uppercase tracking-widest">
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">{isItalian ? 'Squadra' : 'Team'}</th>
                <th className="px-4 py-3 text-center">PG</th>
                <th className="px-4 py-3 text-center">V</th>
                <th className="px-4 py-3 text-center">P</th>
                <th className="px-4 py-3 text-center">S</th>
                <th className="px-4 py-3 text-center">GF</th>
                <th className="px-4 py-3 text-center">GS</th>
                <th className="px-4 py-3 text-center">DR</th>
                <th className="px-4 py-3 text-center font-bold text-[#FFEA00]">PTS</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((team, idx) => (
                <tr key={team.club_id} className={`border-b border-white/5 transition-colors hover:bg-white/5 ${idx === 0 ? 'bg-[#FFEA00]/[0.03]' : ''}`} data-testid={`standing-row-${idx}`}>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold border ${getPositionStyle(idx)}`}>{idx + 1}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <ClubBadge logo={team.club_logo} name={team.club_name} colors={team.club_colors} size="sm" />
                      <span className="font-medium text-sm">{team.club_name}</span>
                      {team.premium && <Crown size={13} className="text-[#FFD700] flex-shrink-0" data-testid={`premium-crown-${team.club_id}`} title="Club Premium" />}
                      {team.forfeit_wins > 0 && (
                        <span
                          title={isItalian ? `${team.forfeit_wins} ${team.forfeit_wins === 1 ? 'vittoria' : 'vittorie'} a tavolino` : `${team.forfeit_wins} forfeit win${team.forfeit_wins === 1 ? '' : 's'}`}
                          data-testid={`forfeit-badge-${team.club_id}`}
                          className="inline-flex items-center gap-0.5 text-[10px] text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded px-1 py-0.5 font-mono"
                        >
                          <Gavel size={10} />{team.forfeit_wins}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-slate-300">{team.played}</td>
                  <td className="px-4 py-3 text-center text-sm text-[#00E676]">{team.won}</td>
                  <td className="px-4 py-3 text-center text-sm text-slate-400">{team.drawn}</td>
                  <td className="px-4 py-3 text-center text-sm text-[#FF1744]">{team.lost}</td>
                  <td className="px-4 py-3 text-center text-sm text-slate-300">{team.goals_for}</td>
                  <td className="px-4 py-3 text-center text-sm text-slate-300">{team.goals_against}</td>
                  <td className="px-4 py-3 text-center text-sm">
                    <span className={team.goal_difference > 0 ? 'text-[#00E676]' : team.goal_difference < 0 ? 'text-[#FF1744]' : 'text-slate-400'}>
                      {team.goal_difference > 0 ? '+' : ''}{team.goal_difference}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-mono font-bold text-lg text-[#FFEA00]">{team.points}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-2">
          {standings.map((team, idx) => (
            <motion.div key={team.club_id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.03 }} className={`bg-[#0a0d15] border border-white/10 p-3 flex items-center gap-3 ${idx === 0 ? 'border-[#FFEA00]/30' : ''}`} data-testid={`standing-card-${idx}`}>
              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold border flex-shrink-0 ${getPositionStyle(idx)}`}>{idx + 1}</span>
              <ClubBadge logo={team.club_logo} name={team.club_name} colors={team.club_colors} size="md" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate flex items-center gap-1">
                  {team.club_name}
                  {team.premium && <Crown size={11} className="text-[#FFD700] flex-shrink-0" />}
                  {team.forfeit_wins > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded px-1 font-mono flex-shrink-0">
                      <Gavel size={9} />{team.forfeit_wins}
                    </span>
                  )}
                </p>
                <p className="text-[10px] text-slate-500">
                  {team.played}PG <span className="text-[#00E676]">{team.won}V</span> {team.drawn}P <span className="text-[#FF1744]">{team.lost}S</span> | GF:{team.goals_for} GS:{team.goals_against}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-mono font-bold text-xl text-[#FFEA00]">{team.points}</p>
                <p className="text-[10px] text-slate-500">{team.goal_difference > 0 ? '+' : ''}{team.goal_difference} DR</p>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="text-[10px] text-slate-600 text-center uppercase tracking-widest">
          PG={isItalian ? 'Giocate' : 'Played'} | V={isItalian ? 'Vinte' : 'Won'} | P={isItalian ? 'Pareggiate' : 'Drawn'} | S={isItalian ? 'Sconfitte' : 'Lost'} | GF={isItalian ? 'Gol Fatti' : 'Goals For'} | GS={isItalian ? 'Gol Subiti' : 'Goals Against'} | DR={isItalian ? 'Diff. Reti' : 'Goal Diff'} | PTS={isItalian ? 'Punti' : 'Points'}
        </p>
        {standings.some((t) => t.forfeit_wins > 0) && (
          <p className="text-[10px] text-amber-400/70 text-center flex items-center justify-center gap-1">
            <Gavel size={10} /> = {isItalian ? 'vittorie a tavolino incluse nel conteggio' : 'forfeit wins included in the count'}
          </p>
        )}
      </>
    )}
  </motion.div>
);
